import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { APIError, api, jsonBody } from "../../api/client";
import type {
  BookingTask,
  EventAllocation,
  EventConflict,
  EventFinances,
  EventRecord,
  ListResponse,
} from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows, membershipAllowsIn } from "../../auth/permissions";
import { formatDateTime, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EditorPage,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Metric,
  Notice,
  StatusPill,
} from "../../ui/components";
import { DetailPageSkeleton, SkeletonTable } from "../../ui/skeletons";
import type { AssetReservation, InventoryReservation } from "../inventory/inventoryTypes";
import { VendorServicesPanel } from "../vendors/VendorServicesPanel";
import { type ConflictResult, EventConflictPanel } from "./EventConflictPanel";
import { EventForm, type EventPayload } from "./EventForm";
import { eventTransitions } from "./eventLifecycle";

export function EventWorkspacePage() {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const membership = auth.session?.selectedMembership;
  // Everything on this page is the client's own programme, so each control is judged by the role
  // held on the events side rather than across both.
  const canManage = membershipAllowsIn(membership, "events", "events.manage");
  // Hiring a supplier in and billing for it are separate from running the programme, because they
  // commit the client's money. Somebody who schedules events is not automatically somebody who
  // signs for the caterer.
  const canHire = membershipAllowsIn(membership, "events", "vendors.manage");
  const canBill = membershipAllowsIn(membership, "events", "invoices.issue");
  // Getting an event ready is the same job whichever side files it, so the shared task capability
  // is asked of the events role here.
  const canManageTasks = membershipAllowsIn(membership, "events", "bookings.tasks.manage");
  // Stock is the client's own kit wherever it is going, so it is asked across both halves, which is
  // how the server judges the reservation routes too.
  const canViewInventory = membershipAllows(membership, "inventory.operate");
  const [editing, setEditing] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [conflicts, setConflicts] = useState<ConflictResult | null>(null);
  const [pending, setPending] = useState<EventPayload | null>(null);
  const [nextStatus, setNextStatus] = useState("");
  const [earlyComplete, setEarlyComplete] = useState<{ status: string; reason: string } | null>(
    null,
  );

  const event = useQuery({
    queryKey: ["events", eventId],
    queryFn: () => api<EventRecord>(`/api/v1/events/${eventId}`),
    enabled: Boolean(eventId),
    retry: false,
  });

  // The money card is asked for only by somebody holding invoice authority, mirroring the
  // server's own gate, so the page never provokes a refusal it can predict.
  const finances = useQuery({
    queryKey: ["events", eventId, "finances"],
    queryFn: () => api<EventFinances>(`/api/v1/events/${eventId}/finances`),
    enabled: Boolean(eventId) && canBill,
  });

  // The jobs and the kit filed under this event itself. A hire the event is delivered under keeps
  // its own lists on the booking, so neither is read through the other.
  const tasks = useQuery({
    queryKey: ["events", eventId, "tasks"],
    queryFn: () => api<ListResponse<BookingTask>>(`/api/v1/events/${eventId}/tasks`),
    enabled: Boolean(eventId) && canManageTasks,
  });
  const reservations = useQuery({
    queryKey: ["inventory", "reservations", "event", eventId],
    queryFn: () =>
      api<ListResponse<InventoryReservation>>(`/api/v1/events/${eventId}/inventory-reservations`),
    enabled: Boolean(eventId) && canViewInventory,
  });
  const assetReservations = useQuery({
    queryKey: ["inventory", "asset-reservations", "event", eventId],
    queryFn: () =>
      api<ListResponse<AssetReservation>>(`/api/v1/events/${eventId}/asset-reservations`),
    enabled: Boolean(eventId) && canViewInventory,
  });

  async function save(payload: EventPayload, overrideReason: string | null) {
    setWorking(true);
    setError(undefined);
    try {
      await api<EventRecord>(`/api/v1/events/${eventId}`, {
        method: "PUT",
        body: jsonBody({
          ...payload,
          confirmConflictOverride: overrideReason !== null,
          conflictOverrideReason: overrideReason ?? "",
        }),
      });
      setConflicts(null);
      setPending(null);
      setEditing(false);
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      await queryClient.invalidateQueries({ queryKey: ["calendar"] });
    } catch (caught) {
      if (caught instanceof APIError && caught.code === "event_conflict") {
        setConflicts({
          conflicts: (caught.details.conflicts as EventConflict[]) ?? [],
          canOverride: Boolean(caught.details.canOverride),
        });
        setPending(payload);
      } else {
        setError(caught);
      }
    } finally {
      setWorking(false);
    }
  }

  async function transition(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (!event.data) return;
    const data = new FormData(formEvent.currentTarget);
    const status = String(data.get("status") ?? "");
    const reason = String(data.get("reason") ?? "");
    // Completing an event that has not finished yet is allowed on purpose: clients close their
    // records their own way. It is confirmed rather than blocked, so early completion is a
    // decision and never a slip.
    if (status === "completed" && new Date(event.data.end).getTime() > Date.now()) {
      setEarlyComplete({ status, reason });
      return;
    }
    await doTransition(status, reason);
  }

  async function doTransition(status: string, reason: string) {
    if (!event.data) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/events/${eventId}/transition`, {
        method: "POST",
        body: jsonBody({ status, reason, version: event.data.version }),
      });
      setNextStatus("");
      setEarlyComplete(null);
      await queryClient.invalidateQueries({ queryKey: ["events"] });
      await queryClient.invalidateQueries({ queryKey: ["calendar"] });
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function addTask(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    const formElement = formEvent.currentTarget;
    const data = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/events/${eventId}/tasks`, {
        method: "POST",
        body: jsonBody({
          title: String(data.get("title") ?? ""),
          category: String(data.get("category") ?? ""),
          assignedRole: String(data.get("assignedRole") ?? ""),
          assignedUserId: null,
          dueAt: data.get("dueAt") ? new Date(String(data.get("dueAt"))).toISOString() : null,
        }),
      });
      formElement.reset();
      await tasks.refetch();
      // The calendar counts open and overdue tasks per event, so it is told as well.
      await queryClient.invalidateQueries({ queryKey: ["calendar"] });
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updateTask(task: BookingTask, status: string) {
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/events/${eventId}/tasks/${task.id}/status`, {
        method: "PUT",
        body: jsonBody({ status }),
      });
      await tasks.refetch();
      await queryClient.invalidateQueries({ queryKey: ["calendar"] });
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  // /events/:id used to mean a booking, because an event WAS a booking. Anything still holding one
  // of those links is asking for a record that exists under its real name, so it is sent there
  // rather than shown a page saying the event cannot be found.
  if (event.error instanceof APIError && event.error.status === 404) {
    return <Navigate to={`/bookings/${eventId}`} replace />;
  }

  const record = event.data;
  const places = record
    ? [
        ...new Set(
          record.allocations.map(
            (allocation) => `${allocation.venueName} · ${allocation.locationName}`,
          ),
        ),
      ]
    : [];
  const available = record ? eventTransitions(record.status) : [];
  const chosenStatus = available.includes(nextStatus) ? nextStatus : (available[0] ?? "");
  const taskItems = tasks.data?.items ?? [];
  const liveReservations = (reservations.data?.items ?? []).filter(
    (item) => item.status !== "cancelled",
  );
  const issuedReservations = liveReservations.filter((item) => item.status === "issued").length;
  const liveAssetReservations = (assetReservations.data?.items ?? []).filter(
    (item) => item.status !== "cancelled",
  );

  return (
    <EditorPage
      title={record?.title ?? "Event"}
      description={
        record
          ? `${record.reference} · ${record.spaceSource === "booking" ? `delivered under ${record.bookingReference}` : "holding its own space"}`
          : "Loading event record"
      }
      backTo="/events"
      backLabel="Back to events"
    >
      {event.isLoading ? (
        <DetailPageSkeleton title="Event" backTo="/events" backLabel="Back to events" />
      ) : null}
      {event.error ? <ErrorPanel error={event.error} /> : null}
      {record ? (
        <div className="stack">
          {error ? <ErrorPanel error={error} /> : null}
          {record.allocations.some((allocation) => allocation.overridden) ? (
            <Notice tone="warning" title="Conflict override present">
              At least one room here was deliberately double-booked. The audit record keeps who
              approved it and why.
            </Notice>
          ) : null}
          <Card className="event-overview-hero">
            <CardHeader
              title="Event overview"
              description="What is being run, when, and in which rooms. Everybody working on it reads this."
              actions={
                <div className="inline">
                  <StatusPill value={record.status} />
                  {canManage ? (
                    <button
                      className="button secondary small"
                      type="button"
                      onClick={() => {
                        setConflicts(null);
                        setPending(null);
                        setEditing((current) => !current);
                      }}
                    >
                      {editing ? "Stop editing" : "Edit event"}
                    </button>
                  ) : null}
                </div>
              }
            />
            <div className="event-summary-strip">
              <div>
                <span>When</span>
                <strong>{formatDateTime(record.start, record.timezone)}</strong>
                <small>to {formatDateTime(record.end, record.timezone)}</small>
              </div>
              <div>
                <span>Where</span>
                <strong>{places.join(", ") || "No room held yet"}</strong>
                <small>{record.timezone}</small>
              </div>
              <div>
                <span>Space</span>
                <strong>
                  {record.spaceSource === "booking" ? "From a booking" : "Held by this event"}
                </strong>
                <small>
                  {record.spaceSource === "booking" && record.bookingId ? (
                    <Link to={`/bookings/${record.bookingId}`}>{record.bookingReference}</Link>
                  ) : (
                    `${record.setupMinutes} minutes setup, ${record.teardownMinutes} teardown`
                  )}
                </small>
              </div>
              <div>
                <span>Attendance</span>
                <strong>{record.attendeeCount ?? "Pending"}</strong>
                <small>Expected</small>
              </div>
            </div>
            <div className="event-brief-grid">
              <div>
                <h3>What is happening</h3>
                <p>{record.summary || "No description has been added yet."}</p>
              </div>
              <div>
                <h3>Internal notes</h3>
                <p>{record.internalNotes || "No internal notes have been added."}</p>
              </div>
              <div>
                <h3>Event lead</h3>
                <p>
                  {record.ownerName || "Nobody is answerable for this event yet."}
                  {record.customerName ? ` · with ${record.customerName}` : ""}
                </p>
              </div>
            </div>
          </Card>

          {canBill && finances.data ? (
            <Card>
              <CardHeader
                title="Money"
                description="The budget, the sponsorship cash raised against it, and what suppliers are costing. In-kind gifts are reported separately and never counted as money to spend."
              />
              <div className="metrics-grid">
                <Metric
                  label="Planned budget"
                  value={
                    finances.data.budgetMinor > 0
                      ? formatMoney(finances.data.budgetMinor, finances.data.currency)
                      : "Not set"
                  }
                  hint={finances.data.budgetMinor > 0 ? undefined : "Set one by editing the event"}
                />
                <Metric
                  label="Sponsor cash in"
                  value={formatMoney(
                    finances.data.sponsorCashReceivedMinor,
                    finances.data.currency,
                  )}
                  hint={`of ${formatMoney(finances.data.sponsorCashPledgedMinor, finances.data.currency)} pledged`}
                />
                <Metric
                  label="Supplier costs"
                  value={formatMoney(finances.data.costsCommittedMinor, finances.data.currency)}
                  hint={`${formatMoney(finances.data.costsPaidMinor, finances.data.currency)} paid so far`}
                />
                <Metric
                  label="Left to spend"
                  value={formatMoney(
                    finances.data.budgetMinor +
                      finances.data.sponsorCashReceivedMinor -
                      finances.data.costsCommittedMinor,
                    finances.data.currency,
                  )}
                  hint="Budget plus sponsor cash in, less costs committed"
                />
              </div>
              {finances.data.sponsorInKindFairValueMinor > 0 ? (
                <p className="muted">
                  In-kind contributions worth{" "}
                  {formatMoney(finances.data.sponsorInKindFairValueMinor, finances.data.currency)}{" "}
                  are recorded against this event and reported apart from cash.
                </p>
              ) : null}
            </Card>
          ) : null}

          <Card>
            <CardHeader
              title="Rooms held"
              description={
                record.spaceSource === "booking"
                  ? "Taken from the booking underneath this event, which is what blocked the rooms."
                  : "Held by this event in the same ledger every booking uses, so nothing else can be let over it."
              }
            />
            {record.allocations.length ? (
              <DataTable
                caption="Rooms this event occupies"
                items={record.allocations}
                getKey={(allocation: EventAllocation) => allocation.id}
                columns={[
                  { label: "Venue", render: (allocation) => allocation.venueName },
                  { label: "Room", render: (allocation) => allocation.locationName },
                  {
                    label: "Held from",
                    render: (allocation) => formatDateTime(allocation.start, record.timezone),
                  },
                  {
                    label: "Held to",
                    render: (allocation) => formatDateTime(allocation.end, record.timezone),
                  },
                  {
                    label: "Turnaround",
                    render: (allocation) =>
                      `${allocation.bufferBeforeMinutes} before, ${allocation.bufferAfterMinutes} after`,
                  },
                  {
                    label: "Overridden",
                    render: (allocation) => (allocation.overridden ? "Yes" : "No"),
                  },
                ]}
              />
            ) : (
              <EmptyState
                title="No room held yet"
                description="This event is planned but takes up no space. Add a room when the venue is decided."
              />
            )}
          </Card>

          {canManageTasks ? (
            <Card>
              <CardHeader
                title="Team readiness"
                description="The jobs that get this event ready, filed under the event itself. A hire it is delivered under keeps its own list on the booking."
              />
              {tasks.isLoading ? (
                <SkeletonTable bare columns={4} rows={3} />
              ) : tasks.error ? (
                <ErrorPanel error={tasks.error} />
              ) : taskItems.length ? (
                <DataTable
                  caption="Event tasks"
                  items={taskItems}
                  getKey={(task: BookingTask) => task.id}
                  columns={[
                    {
                      label: "Task",
                      render: (task) => (
                        <>
                          <strong>{task.title}</strong>
                          <br />
                          <small>{titleCase(task.category)}</small>
                        </>
                      ),
                    },
                    {
                      label: "Assigned",
                      render: (task) =>
                        task.assignedRole ? titleCase(task.assignedRole) : "Anyone",
                    },
                    { label: "Due", render: (task) => formatDateTime(task.dueAt, record.timezone) },
                    {
                      label: "Status",
                      render: (task) => (
                        <select
                          aria-label={`Status for ${task.title}`}
                          value={task.status}
                          disabled={working}
                          onChange={(changeEvent) => updateTask(task, changeEvent.target.value)}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In progress</option>
                          <option value="blocked">Blocked</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Canceled</option>
                        </select>
                      ),
                    },
                  ]}
                />
              ) : (
                <p className="muted">No tasks have been added.</p>
              )}
              <form className="form-grid" onSubmit={addTask}>
                <Field label="Task">
                  <input name="title" required />
                </Field>
                <Field label="Category">
                  <input name="category" defaultValue="general" required />
                </Field>
                <Field label="Assigned role">
                  <select name="assignedRole" defaultValue="operations">
                    <option value="operations">Operations</option>
                    <option value="venue_manager">Venue manager</option>
                    <option value="finance">Finance</option>
                  </select>
                </Field>
                <Field label="Due">
                  <input name="dueAt" type="datetime-local" />
                </Field>
                <div className="full">
                  <FormActions submitting={working} submitLabel="Add task" />
                </div>
              </form>
            </Card>
          ) : null}

          {canViewInventory ? (
            <Card>
              <CardHeader
                title="Resources"
                description="Equipment held for this event in the same stock ledger every hire draws on. Anything held for the hire underneath stays on the booking."
                actions={
                  <Link className="button secondary small" to="/inventory">
                    Open inventory
                  </Link>
                }
              />
              {reservations.error || assetReservations.error ? (
                <ErrorPanel error={reservations.error ?? assetReservations.error} />
              ) : (
                <div className="metrics-grid">
                  <Metric
                    label="Stock reserved"
                    value={reservations.isLoading ? "Loading" : liveReservations.length}
                    hint={`${issuedReservations} issued`}
                  />
                  <Metric
                    label="Assets reserved"
                    value={assetReservations.isLoading ? "Loading" : liveAssetReservations.length}
                  />
                </div>
              )}
            </Card>
          ) : null}

          {/* Engagements hang off whichever record is open. Arranged from here they belong to the
              event, so the invoice they end up on is the event's; arranged from a hire they belong
              to the hire and are recharged to whoever took the room. */}
          <VendorServicesPanel
            owner={{
              kind: "event",
              id: record.id,
              reference: record.reference,
              customerId: record.customerId,
            }}
            canManage={canHire}
            canBill={canBill}
          />

          {canManage && available.length ? (
            <Card>
              <CardHeader
                title="Move this event on"
                description="Every change of state keeps the reason with it, because the record ends up showing only where it stopped."
              />
              <form key={record.version} className="form-stack" onSubmit={transition}>
                <div className="form-grid">
                  <Field label="New status">
                    <select
                      name="status"
                      required
                      value={chosenStatus}
                      onChange={(formEvent) => setNextStatus(formEvent.target.value)}
                    >
                      {available.map((value) => (
                        <option key={value} value={value}>
                          {titleCase(value)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label={chosenStatus === "cancelled" ? "Reason" : "Reason (optional)"}
                    hint={
                      chosenStatus === "cancelled"
                        ? "Canceling needs its reason kept on record: money and people hang off this event."
                        : "Kept with the event for whoever asks later. Leave it empty when the move speaks for itself."
                    }
                  >
                    <textarea
                      name="reason"
                      required={chosenStatus === "cancelled"}
                      minLength={chosenStatus === "cancelled" ? 3 : undefined}
                      maxLength={1000}
                    />
                  </Field>
                </div>
                <FormActions submitting={working} submitLabel="Record the change" />
              </form>
            </Card>
          ) : null}

          {canManage && editing ? (
            conflicts ? (
              <EventConflictPanel
                result={conflicts}
                submitting={working}
                onCancel={() => {
                  setConflicts(null);
                  setPending(null);
                }}
                onOverride={(reason) => {
                  if (pending) void save(pending, reason);
                }}
              />
            ) : (
              <Card>
                <CardHeader
                  title="Revise this event"
                  description="Changing the rooms or the hours re-checks the whole schedule before anything is saved."
                />
                <EventForm
                  mode="edit"
                  initial={record}
                  submitting={working}
                  submitLabel="Save changes"
                  onCancel={() => setEditing(false)}
                  onSubmit={(payload) => void save(payload, null)}
                />
              </Card>
            )
          ) : null}

          {!canManage ? (
            <Notice tone="info">
              Your role can read this event but not change it. Ask somebody who runs the program.
            </Notice>
          ) : null}

          <ConfirmDialog
            open={earlyComplete !== null}
            title="Complete this event early"
            description={`${record.title} is not due to finish until ${formatDateTime(record.end, record.timezone)}. Completing it now closes the record, and the history keeps it as your decision.`}
            confirmLabel="Complete it now"
            submitting={working}
            onClose={() => setEarlyComplete(null)}
            onConfirm={() => {
              if (earlyComplete) void doTransition(earlyComplete.status, earlyComplete.reason);
            }}
          />
        </div>
      ) : null}
    </EditorPage>
  );
}
